import React, { useState } from 'react';
import { api } from '@/lib/api';
import { Lock, Eye, EyeSlash, PencilSimple } from '@phosphor-icons/react';

export function PasswordSection() {
  const [editing, setEditing] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const save = async (e) => {
    e.preventDefault();
    setMessage('');
    if (newPw.length < 6) { setMessage('Password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { setMessage('Passwords do not match'); return; }
    setSaving(true);
    try {
      await api.put('/auth/change-password', { current_password: currentPw, new_password: newPw });
      setMessage('Password changed successfully');
      setEditing(false);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="password-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-outfit text-lg font-semibold text-[#09090B] flex items-center gap-2"><Lock size={20} /> Password</h2>
        {!editing && (
          <button onClick={() => setEditing(true)} data-testid="change-password-button"
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-[#002FA7] font-ibm font-semibold rounded-md hover:bg-[#002FA7]/5 transition-all">
            <PencilSimple size={14} /> Change
          </button>
        )}
      </div>
      {message && <div className={`mb-4 p-3 rounded-md text-sm font-ibm ${message.includes('success') ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`} data-testid="password-message">{message}</div>}
      {editing ? (
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] mb-1 font-ibm">Current Password</label>
            <input data-testid="current-password-input" type={showPw ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)} required
              className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-md font-ibm text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
          <div>
            <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] mb-1 font-ibm">New Password</label>
            <div className="relative">
              <input data-testid="new-password-input" type={showPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} required placeholder="Min 6 characters"
                className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-md font-ibm text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] pr-10" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280]">
                {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] mb-1 font-ibm">Confirm New Password</label>
            <input data-testid="confirm-new-password-input" type={showPw ? 'text' : 'password'} value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required
              className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-md font-ibm text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setEditing(false); setCurrentPw(''); setNewPw(''); setConfirmPw(''); setMessage(''); }} data-testid="cancel-password-button"
              className="px-4 py-2 border border-[#E5E7EB] text-[#09090B] font-ibm text-sm rounded-md hover:bg-[#F3F4F6]">Cancel</button>
            <button data-testid="save-password-button" type="submit" disabled={saving}
              className="px-4 py-2 bg-[#002FA7] text-white font-ibm text-sm font-semibold rounded-md hover:bg-[#00237D] disabled:opacity-50 transition-all">
              {saving ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      ) : (
        <p className="text-sm text-[#6B7280] font-ibm">Last changed: recently</p>
      )}
    </div>
  );
}
